import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as authApi from '../../api/auth';
import { generateTraceId, logAction } from '../../utils/trace';

// Async thunks
export const checkAuth = createAsyncThunk(
  'auth/checkAuth',
  async (args = {}, { rejectWithValue }) => {
    const traceId = args.traceId || generateTraceId();

    try {
      logAction({ type: 'auth/checkAuth/pending', meta: { traceId } });
      const data = await authApi.getMe({ traceId });
      logAction({ type: 'auth/checkAuth/fulfilled', meta: { traceId } }, {
        userId: data.user_id,
        email: data.email,
      });
      return data;
    } catch (error) {
      logAction({ type: 'auth/checkAuth/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const switchTenant = createAsyncThunk(
  'auth/switchTenant',
  async ({ tenantId, traceId: inputTraceId }, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();

    try {
      logAction({ type: 'auth/switchTenant/pending', meta: { traceId } }, {
        tenantId,
      });
      const data = await authApi.switchTenant(tenantId, { traceId });
      logAction({ type: 'auth/switchTenant/fulfilled', meta: { traceId } }, {
        tenantId: data.tenant_id,
      });
      return data;
    } catch (error) {
      logAction({ type: 'auth/switchTenant/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (args = {}) => {
    const traceId = args.traceId || generateTraceId();

    logAction({ type: 'auth/logout/pending', meta: { traceId } });
    await authApi.logout({ traceId });
    logAction({ type: 'auth/logout/fulfilled', meta: { traceId } });
  }
);

export const fetchProfile = createAsyncThunk(
  'auth/fetchProfile',
  async (args = {}, { rejectWithValue }) => {
    const traceId = args.traceId || generateTraceId();

    try {
      logAction({ type: 'auth/fetchProfile/pending', meta: { traceId } });
      const data = await authApi.getProfile({ traceId });
      logAction({ type: 'auth/fetchProfile/fulfilled', meta: { traceId } });
      return data;
    } catch (error) {
      logAction({ type: 'auth/fetchProfile/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async ({ profileData, traceId: inputTraceId }, { rejectWithValue }) => {
    const traceId = inputTraceId || generateTraceId();

    try {
      logAction({ type: 'auth/updateProfile/pending', meta: { traceId } });
      const data = await authApi.updateProfile(profileData, { traceId });
      logAction({ type: 'auth/updateProfile/fulfilled', meta: { traceId } });
      return data;
    } catch (error) {
      logAction({ type: 'auth/updateProfile/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

export const fetchTenantStatus = createAsyncThunk(
  'auth/fetchTenantStatus',
  async (args = {}, { rejectWithValue }) => {
    const traceId = args.traceId || generateTraceId();

    try {
      logAction({ type: 'auth/fetchTenantStatus/pending', meta: { traceId } });
      const data = await authApi.getTenantStatus({ traceId });
      logAction({ type: 'auth/fetchTenantStatus/fulfilled', meta: { traceId } });
      return data;
    } catch (error) {
      logAction({ type: 'auth/fetchTenantStatus/rejected', meta: { traceId } }, {
        error: error.message,
      });
      return rejectWithValue(error.message);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    tenants: [],
    activeTenant: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
    // Profile state
    profile: null,
    profileLoading: false,
    profileError: null,
    // Tenant status state (for users without tenants)
    tenantStatus: null,
    tenantStatusLoading: false,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.profileError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // checkAuth
      .addCase(checkAuth.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = {
          id: action.payload.user_id,
          email: action.payload.email,
          isGodMode: action.payload.is_god_mode,
        };
        state.tenants = action.payload.tenants || [];
        // activeTenant might be null if user has 0 or multiple tenants
        state.activeTenant = action.payload.tenants?.find(
          t => t.tenant_id === action.payload.active_tenant_id
        ) || null;
      })
      .addCase(checkAuth.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.error = action.payload;
      })
      // switchTenant
      .addCase(switchTenant.fulfilled, (state, action) => {
        state.activeTenant = state.tenants.find(
          t => t.tenant_id === action.payload.tenant_id
        );
      })
      // logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.tenants = [];
        state.activeTenant = null;
        state.isAuthenticated = false;
        state.profile = null;
        state.tenantStatus = null;
      })
      // fetchProfile
      .addCase(fetchProfile.pending, (state) => {
        state.profileLoading = true;
        state.profileError = null;
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.profile = action.payload;
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.profileLoading = false;
        state.profileError = action.payload;
      })
      // updateProfile
      .addCase(updateProfile.pending, (state) => {
        state.profileLoading = true;
        state.profileError = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.profile = action.payload;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.profileLoading = false;
        state.profileError = action.payload;
      })
      // fetchTenantStatus
      .addCase(fetchTenantStatus.pending, (state) => {
        state.tenantStatusLoading = true;
      })
      .addCase(fetchTenantStatus.fulfilled, (state, action) => {
        state.tenantStatusLoading = false;
        state.tenantStatus = action.payload;
      })
      .addCase(fetchTenantStatus.rejected, (state) => {
        state.tenantStatusLoading = false;
      });
  },
});

export const { clearError } = authSlice.actions;
export default authSlice.reducer;
